// assets/toast-notification.js

class ToastNotification extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Initialize containers for different positions
    this.containers = {};

    // Initialize containers for standard positions
    const positions = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];
    positions.forEach((position) => {
      const container = document.createElement('div');
      container.className = `toasts-container toasts-container--${position}`;
      container.setAttribute('role', 'alert');
      container.setAttribute('aria-live', 'assertive');
      container.setAttribute('aria-atomic', 'false');
      container.style.pointerEvents = 'none'; // Ensure container doesn't block interactions
      this.shadowRoot.appendChild(container);
      this.containers[position] = container;
    });

    // Listen for custom 'toast' events
    this.handleToastEvent = this.showToast.bind(this);
    document.addEventListener('toast', this.handleToastEvent);
  }

  disconnectedCallback() {
    // Clean up the event listener when the element is removed from the DOM
    document.removeEventListener('toast', this.handleToastEvent);
  }

  /**
   * Displays a toast notification with the given options.
   * @param {CustomEvent} event - The toast event containing detail.
   */
  showToast(event) {
    const options = event.detail;

    // Destructure properties from options.detail with default values
    const {
      title = 'Notification',
      message = '',
      status = 'success',
      position = 'top-right',
      size = 'small',
      duration = 3000,
    } = options;

    //console.log('Received toast options:', { title, message, status, position, size, duration }); // For debugging

    // Validate position
    const validPositions = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];
    const toastPosition = validPositions.includes(position) ? position : 'top-right';

    // Ensure the container exists for the position
    let container = this.containers[toastPosition];
    if (!container) {
      container = document.createElement('div');
      container.className = `toasts-container toasts-container--${toastPosition}`;
      container.setAttribute('role', 'alert');
      container.setAttribute('aria-live', 'assertive');
      container.setAttribute('aria-atomic', 'false');
      container.style.pointerEvents = 'none';
      this.shadowRoot.appendChild(container);
      this.containers[toastPosition] = container;
    }

    // Enforce maximum number of toasts per container
    const MAX_TOASTS = 5;
    if (container.children.length >= MAX_TOASTS) {
      this.removeToast(container.firstElementChild);
    }

    // Create the toast element
    const toast = document.createElement('div');
    toast.classList.add('toast', `toast--${status}`, `toast--${size}`);
    toast.setAttribute('tabindex', '0'); // Make toast focusable for accessibility

    toast.innerHTML = `
      <div class="toast__content">
        <strong class="toast__title" role="heading" aria-level="2">${this.escapeHTML(title)}</strong>
        <p class="toast__message">${this.escapeHTML(message)}</p>
      </div>
      <button class="toast__close" aria-label="Close notification">&times;</button>
    `;

    container.appendChild(toast);

    // Trigger show animation
    requestAnimationFrame(() => {
      toast.classList.add('toast--show');
      toast.focus(); // Optionally focus the toast for screen readers
    });

    // Close functionality
    const closeButton = toast.querySelector('.toast__close');

    // Define the click handler separately to enable proper removal
    const handleClose = () => {
      this.removeToast(toast);
    };

    closeButton.addEventListener('click', handleClose);

    // Remove the event listener when toast is removed to avoid memory leaks
    const handleTransitionEnd = (event) => {
      if (event.propertyName === 'opacity' && toast.classList.contains('toast--hide')) {
        closeButton.removeEventListener('click', handleClose);
        toast.removeEventListener('transitionend', handleTransitionEnd);
      }
    };

    toast.addEventListener('transitionend', handleTransitionEnd);

    // Auto-remove after duration
    setTimeout(() => {
      this.removeToast(toast);
    }, duration);
  }

  /**
   * Removes a toast element with a hide animation.
   * @param {HTMLElement} toast - The toast element to remove.
   */
  removeToast(toast) {
    if (!toast) return;
    toast.classList.remove('toast--show');
    toast.classList.add('toast--hide');

    // Get transition duration in milliseconds
    const computedStyle = getComputedStyle(toast);
    let transitionDuration = parseFloat(computedStyle.transitionDuration) * 1000;
    if (isNaN(transitionDuration)) {
      transitionDuration = 300; // Fallback to 300ms
    }

    setTimeout(() => {
      toast.remove();
    }, transitionDuration);
  }

  /**
   * Escapes HTML to prevent XSS attacks.
   * @param {string} str - The string to escape.
   * @returns {string} - The escaped string.
   */
  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  connectedCallback() {
    // Add styles to the shadow root
    const style = document.createElement('style');
    style.textContent = this.getStyles();
    this.shadowRoot.appendChild(style);
  }

  getStyles() {
    return `
      /* CSS Variables for easy customization */
      :host {
        --toast-success-color: #00AFAA;
        --toast-error-color: #EA6852;
        --toast-warning-color: #ff9800;
        --toast-info-color: #005677;
        --toast-bg-color: #fff;
        --toast-text-color: #333;
        --toast-close-color: #999;
        --toast-close-hover-color: #333;
        --toast-padding: 1rem;
        --toast-margin: 0.5rem 0;
        --toast-border-radius: 4px;
        --toast-box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        --toast-transition-duration: 0.3s;
        --toast-width-small: 250px;
        --toast-width-large: 400px;
        --toast-z-index: 9999;
      }

      /* Container Styles */
      .toasts-container {
        position: fixed;
        z-index: var(--toast-z-index);
        display: flex;
        flex-direction: column;
        pointer-events: none;
      }

      /* Positioning */
      .toasts-container--top-right {
        top: 1rem;
        right: 1rem;
        align-items: flex-end;
      }
      .toasts-container--top-left {
        top: 1rem;
        left: 1rem;
        align-items: flex-start;
      }
      .toasts-container--bottom-right {
        bottom: 1rem;
        right: 1rem;
        align-items: flex-end;
      }
      .toasts-container--bottom-left {
        bottom: 1rem;
        left: 1rem;
        align-items: flex-start;
      }

      /* Toast Styles */
      .toast {
        display: flex;
        align-items: center;
        background-color: var(--toast-bg-color);
        color: var(--toast-text-color);
        padding: var(--toast-padding);
        margin: var(--toast-margin);
        border-radius: var(--toast-border-radius);
        box-shadow: var(--toast-box-shadow);
        opacity: 0;
        transform: translateY(20px);
        transition: opacity var(--toast-transition-duration) ease, transform var(--toast-transition-duration) ease;
        pointer-events: auto;
        width: var(--toast-width-small);
        max-width: 90vw; /* Responsive width */
      }

      .toast--show {
        opacity: 1;
        transform: translateY(0);
      }

      .toast--hide {
        opacity: 0;
        transform: translateY(-20px);
      }

      /* Status Styles */
      .toast--success {
        border-left: 4px solid var(--toast-success-color);
      }

      .toast--error {
        border-left: 4px solid var(--toast-error-color);
      }

      .toast--warning {
        border-left: 4px solid var(--toast-warning-color);
      }

      .toast--info {
        border-left: 4px solid var(--toast-info-color);
      }

      /* Size Styles */
      .toast--small {
        width: var(--toast-width-small);
      }

      .toast--large {
        width: var(--toast-width-large);
      }

      /* Content Styles */
      .toast__title {
        margin: 0;
        font-weight: bold;
      }

      .toast__message {
        margin: 0.5rem 0 0 0;
      }

      .toast__content {
        flex: 1;
      }

      .toast__close {
        background: none;
        border: none;
        font-size: 1.5rem;
        line-height: 1;
        margin-left: 1rem;
        cursor: pointer;
        color: var(--toast-close-color);
      }

      .toast__close:hover {
        color: var(--toast-close-hover-color);
      }

      /* Responsive Design */
      @media (max-width: 600px) {
        .toast {
          width: 90vw;
        }
      }
    `;
  }
}

// Define the custom element
customElements.define('toast-notification', ToastNotification);

// Append the toast-notification element to the body
document.addEventListener('DOMContentLoaded', () => {
  const existingToastElement = document.querySelector('toast-notification');
  if (!existingToastElement) {
    document.body.appendChild(document.createElement('toast-notification'));
  }
});
